// src/components/dashboard components/forms/CareerOpportunitiesForm.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Save, Plus, X } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CareerOpportunitiesForm = () => {
  const [formData, setFormData] = useState({
    career_opportunities_id: 0,
    title: "",
    subtitle: "",
    description: "",
  });

  const [roles, setRoles] = useState([]);
  const [newRole, setNewRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // =========================
  // Fetch existing data
  // =========================
  useEffect(() => {
    fetchCareerOpportunities();
  }, []);

  const fetchCareerOpportunities = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/career/admin/opportunities`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data && response.data.length > 0) {
        const data = response.data[0];

        // Base fields
        setFormData({
          career_opportunities_id: data.career_opportunities_id || 0,
          title: data.title || "",
          subtitle: data.subtitle || "",
          description: data.description || "",
        });

        // Parse we_hire_for from TEXT or legacy JSON
        let parsedRoles = [];
        if (data.we_hire_for) {
          const raw = String(data.we_hire_for).trim();

          // Legacy JSON support
          if (raw.startsWith("[") && raw.endsWith("]")) {
            try {
              const arr = JSON.parse(raw);
              if (Array.isArray(arr)) {
                parsedRoles = arr
                  .map((r) => String(r).trim())
                  .filter((r) => r.length > 0);
              }
            } catch {
              parsedRoles = [];
            }
          }

          // Plain TEXT support: comma / newline
          if (parsedRoles.length === 0) {
            parsedRoles = raw
              .split(/\r?\n|,/)
              .map((item) => item.trim())
              .filter((item) => item.length > 0);
          }
        }

        setRoles(parsedRoles);
      }
    } catch (error) {
      console.error("Error fetching career opportunities:", error);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Handlers
  // =========================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const addRole = () => {
    const trimmed = newRole.trim();
    if (!trimmed) return;

    setRoles((prev) =>
      prev.includes(trimmed) ? prev : [...prev, trimmed]
    );
    setNewRole("");
  };

  const removeRole = (index) => {
    setRoles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      alert("Title is required!");
      return;
    }

    setSaveLoading(true);
    try {
      const token = localStorage.getItem("token");

      // Combine roles from list + unsaved input (if user forgot to click Add)
      const combinedRoles = [...roles];
      const pending = newRole.trim();
      if (pending && !combinedRoles.includes(pending)) {
        combinedRoles.push(pending);
      }

      const we_hire_for_text =
        combinedRoles.length > 0 ? combinedRoles.join(", ") : null;

      const dataToSave = {
        ...formData,
        we_hire_for: we_hire_for_text,
      };

      console.log("🔹 Saving opportunities payload:", dataToSave);

      await axios.post(
        `${API_BASE_URL}/career/admin/opportunities/save`,
        dataToSave,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      alert("Career opportunities saved successfully!");
      setNewRole(""); // clear input after save
      fetchCareerOpportunities();
    } catch (error) {
      console.error("Error saving career opportunities:", error);
      alert("Failed to save career opportunities. Please check console/logs.");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleClearWeHireFor = async () => {
    if (!formData.career_opportunities_id) {
      alert("No record found to clear!");
      return;
    }

    if (!window.confirm("Are you sure you want to clear all hiring roles?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API_BASE_URL}/career/admin/opportunities/we-hire-for/${formData.career_opportunities_id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      alert("Hiring roles cleared successfully!");
      setRoles([]);
      setNewRole("");
      fetchCareerOpportunities();
    } catch (error) {
      console.error("Error clearing we_hire_for:", error);
      alert("Failed to clear hiring roles. Please check console/logs.");
    }
  };

  // =========================
  // Loading UI
  // =========================
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // =========================
  // Form UI
  // =========================
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">
        Career Opportunities Section
      </h2>

      {/* Title */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="Enter section title"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
        />
      </div>

      {/* Subtitle */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Subtitle
        </label>
        <input
          type="text"
          name="subtitle"
          value={formData.subtitle}
          onChange={handleChange}
          placeholder="Enter section subtitle"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Description
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Enter section description"
          rows="4"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
        />
      </div>

      {/* We Hire For */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          We Hire For (Roles)
        </label>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addRole();
              }
            }}
            placeholder="Enter a role (e.g., Sales Executive)"
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
          />
          <button
            type="button"
            onClick={addRole}
            className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition flex items-center gap-2"
          >
            <Plus size={20} />
            Add
          </button>
        </div>

        {/* Roles List */}
        {roles.length > 0 && (
          <div className="space-y-2 mb-3">
            {roles.map((role, index) => (
              <div
                key={index}
                className="flex items-center justify-between bg-gray-50 px-4 py-2 rounded-lg border border-gray-200"
              >
                <span className="text-gray-700">{role}</span>
                <button
                  type="button"
                  onClick={() => removeRole(index)}
                  className="text-red-500 hover:text-red-700 transition"
                >
                  <X size={20} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Clear All Button */}
        {roles.length > 0 && (
          <button
            type="button"
            onClick={handleClearWeHireFor}
            className="text-sm text-red-600 hover:text-red-800 underline"
          >
            Clear all hiring roles
          </button>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-4">
        <button
          type="button"
          onClick={handleSave}
          disabled={saveLoading}
          className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          <Save size={20} />
          {saveLoading ? "Saving..." : "Save Opportunities Section"}
        </button>
      </div>
    </div>
  );
};

export default CareerOpportunitiesForm;
